import { ThemeToggle } from "./ThemeToggle";
import type { ThemeName } from "../hooks/useTheme";

export type HealthState = "checking" | "online" | "offline" | "unauthorized";

export function Header({
  theme,
  onToggleTheme,
  status,
}: {
  theme: ThemeName;
  onToggleTheme: () => void;
  status: HealthState;
}) {
  return (
    <header className="app-header">
      <svg width="40" height="40" viewBox="0 0 32 32" aria-hidden>
        <polygon
          points="16,2 30,10 30,22 16,30 2,22 2,10"
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2"
        />
        <polygon
          points="16,9 23,13 23,19 16,23 9,19 9,13"
          fill="var(--accent)"
          opacity="0.9"
        />
      </svg>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1 className="app-title">MAGI</h1>
        <span className="app-subtitle">
          Multi · Agent · Group · Intelligence
        </span>
      </div>
      <div style={{ flex: 1 }} />
      <ServiceStatus status={status} />
      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </header>
  );
}

function ServiceStatus({ status }: { status: HealthState }) {
  const { color, glyph, label, tooltip } = (() => {
    switch (status) {
      case "online":
        return {
          color: "var(--positive)",
          glyph: "⬡",
          label: "Online",
          tooltip: "MAGI-Service reachable; token accepted",
        };
      case "offline":
        return {
          color: "var(--negative)",
          glyph: "⚠",
          label: "Offline",
          tooltip: "MAGI-Service unreachable",
        };
      case "unauthorized":
        return {
          color: "var(--accent)",
          glyph: "⚠",
          label: "Token rejected",
          tooltip:
            "MAGI-Service rejected the service token (401/403). Check MAGI_SERVICE_TOKEN.",
        };
      default:
        return {
          color: "var(--text-mute)",
          glyph: "◌",
          label: "Checking…",
          tooltip: "Checking MAGI-Service health",
        };
    }
  })();
  return (
    <span
      role="status"
      aria-label={label}
      title={tooltip}
      data-testid="service-status"
      data-state={status}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
        fontWeight: 600,
        color: "var(--text-dim)",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      <span
        aria-hidden
        style={{
          color,
          fontSize: 14,
          textShadow: status === "online" ? `0 0 10px ${color}` : "none",
          transition: "color 0.3s, text-shadow 0.3s",
        }}
      >
        {glyph}
      </span>
      {label}
    </span>
  );
}
